import React, { useState, useEffect } from 'react';
import { Modal, Button, Table, Select, InputNumber, Checkbox, message, Typography, Space, Tag } from 'antd';
import { LinkOutlined, ShopOutlined } from '@ant-design/icons';
import customerOrderService from '../services/customerOrderService.ts';
import { inputNumberFormatter, inputNumberParser } from '../utils/numberFormatting.ts';

const { Title, Text } = Typography;
const { Option } = Select;

interface ProductWithoutSupplier {
  product_id: number;
  product_code: string;
  product_description: string;
  requested_quantity: number;
  shortage: number;
  available_stock: number;
  reserved_stock: number;
}

interface Supplier {
  id: number;
  name: string;
  supplier_code: string;
  contact_person?: string;
  email?: string;
  phone?: string;
  city?: string;
  country?: string;
}

interface SupplierAssociation {
  product_id: number;
  supplier_id: number;
  cost?: number;
  lead_time_days?: number;
  minimum_order_quantity?: number;
  is_primary: boolean;
}

interface SupplierAssociationModalProps {
  visible: boolean;
  onClose: () => void;
  customerOrderId: number;
  customerOrderNumber: string;
  productsWithoutSuppliers: ProductWithoutSupplier[];
  onAssociationsComplete: (associations: SupplierAssociation[]) => void;
}

interface ProductAssociationForm extends ProductWithoutSupplier {
  supplier_id?: number;
  cost?: number;
  lead_time_days?: number;
  minimum_order_quantity?: number;
  is_primary?: boolean;
  available_suppliers?: Supplier[];
  loading_suppliers?: boolean;
}

const SupplierAssociationModal: React.FC<SupplierAssociationModalProps> = ({
  visible,
  onClose,
  customerOrderId,
  customerOrderNumber,
  productsWithoutSuppliers,
  onAssociationsComplete,
}) => {
  const [loading, setLoading] = useState(false);
  const [productsData, setProductsData] = useState<ProductAssociationForm[]>([]);

  useEffect(() => {
    if (visible && productsWithoutSuppliers.length > 0) {
      initializeProductsData();
    }
  }, [visible, productsWithoutSuppliers]);

  const initializeProductsData = async () => {
    const updatedProducts: ProductAssociationForm[] = [];
    
    for (const product of productsWithoutSuppliers) {
      updatedProducts.push({
        ...product,
        cost: undefined,
        lead_time_days: 30,
        minimum_order_quantity: 1,
        is_primary: true,
        available_suppliers: [],
        loading_suppliers: true
      });
    }
    
    setProductsData(updatedProducts);

    // Carica fornitori disponibili per ogni prodotto
    for (let i = 0; i < productsWithoutSuppliers.length; i++) {
      const product = productsWithoutSuppliers[i];
      try {
        const suppliersResponse = await customerOrderService.getAvailableSuppliers(
          customerOrderId, 
          product.product_id
        );
        
        setProductsData(prevData => {
          const newData = [...prevData];
          newData[i] = {
            ...newData[i],
            available_suppliers: suppliersResponse.available_suppliers || [],
            loading_suppliers: false
          };
          return newData;
        });
      } catch (error) {
        console.error(`Error loading suppliers for product ${product.product_id}:`, error);
        setProductsData(prevData => {
          const newData = [...prevData];
          newData[i] = {
            ...newData[i],
            available_suppliers: [],
            loading_suppliers: false
          };
          return newData;
        });
      }
    }
  };

  const handleFieldChange = (
    productId: number,
    field: 'supplier_id' | 'cost' | 'lead_time_days' | 'minimum_order_quantity' | 'is_primary',
    value: number | boolean | null | undefined
  ) => {
    setProductsData(prevData =>
      prevData.map(product =>
        product.product_id === productId
          ? { ...product, [field]: value ?? undefined }
          : product
      )
    );
  };


  const handleConfirm = async () => {
    try {
      // Validazione: tutti i prodotti devono avere un fornitore selezionato
      const missingSuppliers = productsData.filter(product => !product.supplier_id);
      if (missingSuppliers.length > 0) {
        message.error('Selezionare un fornitore per tutti i prodotti prima di procedere');
        return;
      }

      setLoading(true);

      // Valori inseriti nella tabella (default: costo 0 → l'ordine fornitore userà il
      // costo di listino del prodotto; consegna 30 gg; minimo 1; fornitore primario)
      const associations: SupplierAssociation[] = productsData.map(product => ({
        product_id: product.product_id,
        supplier_id: product.supplier_id!,
        cost: product.cost ?? 0,
        lead_time_days: product.lead_time_days ?? 30,
        minimum_order_quantity: product.minimum_order_quantity ?? 1,
        is_primary: product.is_primary ?? true
      }));

      onAssociationsComplete(associations);

    } catch (error: any) {
      message.error('Errore durante la creazione delle associazioni: ' + (error.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setProductsData([]);
    onClose();
  };

  const columns = [
    {
      title: 'Prodotto',
      key: 'product',
      width: '30%',
      render: (_: any, record: ProductAssociationForm) => (
        <div>
          <Text strong>{record.product_code}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {record.product_description}
          </Text>
          <br />
          <Space size="small" style={{ marginTop: 4 }}>
            <Tag color="orange">Richiesti: {record.requested_quantity}</Tag>
            <Tag color="red">Mancanti: {record.shortage}</Tag>
          </Space>
        </div>
      ),
    },
    {
      title: 'Fornitore',
      key: 'supplier',
      width: '28%',
      render: (_: any, record: ProductAssociationForm) => (
        <Select
          style={{ width: '100%' }}
          placeholder="Seleziona fornitore"
          loading={record.loading_suppliers}
          value={record.supplier_id}
          onChange={(value) => handleFieldChange(record.product_id, 'supplier_id', value)}
          showSearch
          filterOption={(input, option) =>
            (option?.children as unknown as string)?.toLowerCase().indexOf(input.toLowerCase()) >= 0
          }
        >
          {record.available_suppliers?.map((supplier) => (
            <Option key={supplier.id} value={supplier.id}>
              <div>
                <div>
                  <Text strong>{supplier.name}</Text>
                  {supplier.supplier_code && (
                    <Text type="secondary" style={{ marginLeft: 8, fontSize: '12px' }}>
                      ({supplier.supplier_code})
                    </Text>
                  )}
                </div>
                {supplier.city && (
                  <div>
                    <Text type="secondary" style={{ fontSize: '11px' }}>
                      {supplier.city}{supplier.country && `, ${supplier.country}`}
                    </Text>
                  </div>
                )}
              </div>
            </Option>
          ))}
        </Select>
      ),
    },
    {
      title: 'Costo unitario (€)',
      key: 'cost',
      width: '14%',
      render: (_: any, record: ProductAssociationForm) => (
        <InputNumber
          style={{ width: '100%' }}
          min={0}
          step={0.01}
          precision={4}
          formatter={inputNumberFormatter}
          parser={inputNumberParser}
          placeholder="0,0000"
          value={record.cost}
          onChange={(value) => handleFieldChange(record.product_id, 'cost', value)}
        />
      ),
    },
    {
      title: 'Consegna (gg)',
      key: 'lead_time_days',
      width: '10%',
      render: (_: any, record: ProductAssociationForm) => (
        <InputNumber
          style={{ width: '100%' }}
          min={0}
          placeholder="Giorni"
          value={record.lead_time_days}
          onChange={(value) => handleFieldChange(record.product_id, 'lead_time_days', value)}
        />
      ),
    },
    {
      title: 'Q.tà minima',
      key: 'minimum_order_quantity',
      width: '10%',
      render: (_: any, record: ProductAssociationForm) => (
        <InputNumber
          style={{ width: '100%' }}
          min={1}
          value={record.minimum_order_quantity}
          onChange={(value) => handleFieldChange(record.product_id, 'minimum_order_quantity', value)}
        />
      ),
    },
    {
      title: 'Primario',
      key: 'is_primary',
      width: '8%',
      align: 'center' as const,
      render: (_: any, record: ProductAssociationForm) => (
        <Checkbox
          checked={record.is_primary ?? true}
          onChange={(e) => handleFieldChange(record.product_id, 'is_primary', e.target.checked)}
        />
      ),
    },
  ];

  return (
    <Modal
      title={
        <div>
          <LinkOutlined style={{ marginRight: 8, color: '#1890ff' }} />
          Associa Fornitori ai Prodotti
        </div>
      }
      open={visible}
      onCancel={handleClose}
      width={1200}
      footer={[
        <Button key="cancel" onClick={handleClose} disabled={loading}>
          Annulla
        </Button>,
        <Button key="confirm" type="primary" onClick={handleConfirm} loading={loading}>
          Conferma Associazioni e Converti
        </Button>,
      ]}
    >
      <div style={{ marginBottom: 16 }}>
        <div style={{ textAlign: 'center', marginBottom: 16 }}>
          <ShopOutlined style={{ fontSize: 32, color: '#faad14', marginBottom: 8 }} />
          <Title level={4} style={{ marginBottom: 8 }}>
            Associazione Fornitori Richiesta
          </Title>
          <Text>
            L'ordine cliente <strong>{customerOrderNumber}</strong> contiene prodotti senza fornitori associati.
          </Text>
        </div>

        <div style={{ backgroundColor: '#fff7e6', border: '1px solid #ffd591', borderRadius: 6, padding: 16, marginBottom: 16 }}>
          <Title level={5} style={{ color: '#d46b08', marginBottom: 8 }}>
            <LinkOutlined /> Cosa fare:
          </Title>
          <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
            <li>Seleziona un fornitore per ogni prodotto dalla lista disponibile</li>
            <li>Imposta costo unitario, giorni di consegna, quantità minima d'ordine e se il fornitore è quello primario (costo a 0 = l'ordine fornitore userà il costo di listino del prodotto)</li>
            <li>Le associazioni verranno salvate nell'anagrafica prodotto (scheda Fornitori) e restano modificabili da lì</li>
            <li>Dopo l'associazione, l'ordine verrà automaticamente convertito in ordini fornitori</li>
          </ul>
        </div>
      </div>

      <Table
        columns={columns}
        dataSource={productsData}
        rowKey="product_id"
        pagination={false}
        size="middle"
        scroll={{ y: 400 }}
      />

      <div style={{ marginTop: 16, textAlign: 'center' }}>
        <Text type="secondary">
          {productsData.length} prodotto{productsData.length !== 1 ? 'i' : ''} necessita{productsData.length === 1 ? '' : 'no'} di associazione fornitore
        </Text>
      </div>
    </Modal>
  );
};

export default SupplierAssociationModal;